import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ArrowRight, Lock, User as UserIcon, Lightning } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#050505] text-zinc-50">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-zinc-900">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/[0.04] via-transparent to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 flex items-center justify-center">
              <Lightning size={18} weight="fill" className="text-zinc-950" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">UNDERADS<span className="text-orange-500"> METAVERSE</span></span>
          </div>

          <div className="space-y-6">
            <div className="text-xs font-mono tracking-[0.3em] text-orange-500 uppercase">// Marketing Operations</div>
            <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95]">
              Talk to your<br />
              <span className="text-orange-500">Meta Ads.</span><br />
              Get answers.
            </h1>
            <p className="text-zinc-400 max-w-md leading-relaxed text-sm">
              Powered by Claude Opus 4.6 with 1M token context. Create campaigns, analyze performance, and act — all by chat.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 text-xs font-mono">
            <div className="border-l-2 border-orange-500/40 pl-3">
              <div className="text-zinc-500 uppercase tracking-wider mb-1">Model</div>
              <div className="text-zinc-300">Opus 4.6</div>
            </div>
            <div className="border-l-2 border-orange-500/40 pl-3">
              <div className="text-zinc-500 uppercase tracking-wider mb-1">Context</div>
              <div className="text-zinc-300">1M tokens</div>
            </div>
            <div className="border-l-2 border-orange-500/40 pl-3">
              <div className="text-zinc-500 uppercase tracking-wider mb-1">Mode</div>
              <div className="text-emerald-400">Sandbox</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm" data-testid="login-form-container">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-7 h-7 bg-orange-500 flex items-center justify-center">
              <Lightning size={16} weight="fill" className="text-zinc-950" />
            </div>
            <span className="font-display font-bold text-base">UNDERADS<span className="text-orange-500"> METAVERSE</span></span>
          </div>

          <div className="text-xs font-mono tracking-[0.3em] text-zinc-500 uppercase mb-3">// Sign In</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-2">Welcome back.</h2>
          <p className="text-sm text-zinc-500 mb-10">Enter your credentials to access the platform.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 block mb-2">Username</label>
              <div className="relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  data-testid="login-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-zinc-800 pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder="Admin"
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 block mb-2">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-zinc-800 pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-3 py-2 font-mono">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-zinc-950 font-semibold text-sm py-2.5 transition-colors flex items-center justify-center gap-2 group"
            >
              {loading ? "Signing in..." : <>Sign In <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></>}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-900">
            <div className="text-xs font-mono text-zinc-600">
              Internal team access only. Contact your admin for credentials.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
